// ================================
// IMPORTS
// ================================
import { defineStore } from "pinia"
import { useAuthStore } from "./auth.store"
import { useBuildingStore } from "./building-v2.store"
import { useSourceStore } from "./source.store"
import { useConfigStore } from "./config.store"
import { useTaskStore } from "./task.store"
import { useNavigationStore } from "./navigation.store"

import { useGraphStore } from "@modules/data/stores/graph.store"
import { useComparatorStore } from "@modules/courtage/stores/comparator.store"
import { useIrveStore } from "@modules/irve/stores/irve.store"
import { useEmConsoCompareStore } from "@modules/energy-manager/stores/em-conso-compare.store"
import { useAlarmeStore } from "@modules/energy-manager/stores/alarme.store"
import { useSubscriptionFormStore } from "@modules/abonnement/stores/subscription-form.store"

/**
 * Store racine pour gérer les actions globales de l'application
 */
export const useRootStore = defineStore("root", () => {
    // ================================
    // ACTIONS GLOBALES
    // ================================

    /**
     * Reset tous les stores de l'application
     * @param excludeStores - Liste des stores à exclure du reset (par défaut: aucun)
     * Stores disponibles: 'auth', 'building', 'source', 'config', 'task', 'navigation', 'graph', 'comparator', 'irve', 'emConsoCompare', 'alarme', 'subscriptionForm'
     * @param resetScope - Si true, réinitialise le scope (par défaut: false)
     */
    function resetAllStores(excludeStores: string[] = [], resetScope = false) {
        // Reset du store auth
        if (!excludeStores.includes("auth")) {
            const authStore = useAuthStore()
            authStore.$reset()
        }

        // Reset du store building
        if (!excludeStores.includes("building")) {
            const buildingStore = useBuildingStore()
            buildingStore.$reset()
        }

        // Reset du store source
        if (!excludeStores.includes("source")) {
            const sourceStore = useSourceStore()
            sourceStore.$reset()
        }

        // Reset du store config
        if (!excludeStores.includes("config")) {
            const configStore = useConfigStore()
            configStore.$reset(resetScope)
        }

        // Reset du store task
        if (!excludeStores.includes("task")) {
            try {
                const taskStore = useTaskStore()
                taskStore.$reset()
            } catch {
                // Le store task peut ne pas être initialisé
            }
        }

        // Reset du store navigation (scroll / layout)
        if (!excludeStores.includes("navigation")) {
            const navigationStore = useNavigationStore()
            navigationStore.$reset()
        }

        // Reset du store graph
        if (!excludeStores.includes("graph")) {
            const graphStore = useGraphStore()
            graphStore.$reset()
        }

        // Reset du store courtage
        if (!excludeStores.includes("comparator")) {
            const comparatorStore = useComparatorStore()
            comparatorStore.$reset()
        }

        // Reset du store irve
        if (!excludeStores.includes("irve")) {
            const irveStore = useIrveStore()
            irveStore.$reset()
        }

        // Reset du store comparaison conso EM
        if (!excludeStores.includes("emConsoCompare")) {
            const emConsoCompareStore = useEmConsoCompareStore()
            emConsoCompareStore.$reset()
        }

        // Reset du store alarmes EM (état UI local)
        if (!excludeStores.includes("alarme")) {
            const alarmeStore = useAlarmeStore()
            alarmeStore.$reset()
        }

        // Reset du formulaire d'abonnement / inscription
        if (!excludeStores.includes("subscriptionForm")) {
            const subscriptionFormStore = useSubscriptionFormStore()
            subscriptionFormStore.$reset()
        }
    }

    /**
     * Réinitialise le store à son état initial.
     * Le store root n'a pas d'état réactif, cette fonction est un no-op pour l'API uniforme.
     */
    function $reset() {
        // Pas d'état à réinitialiser dans le store root
    }

    // ================================
    // EXPORT STORE
    // ================================

    return {
        resetAllStores,
        $reset,
    }
})
